// ============================================
// FILE: /frontend/src/components/modals/UpgradeLimitModal.jsx
// PURPOSE: Modal shown when user exceeds tier listing limits
// ============================================

import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertCircle, TrendingUp, Trash2 } from 'lucide-react';
import { getTierInfo, getTierBadgeColor } from '../../utils/tierCheck';

/**
 * UpgradeLimitModal Component
 * Shows when user tries to create listing but has exceeded tier limits
 * 
 * @param {Boolean} open - Modal open state
 * @param {Function} onClose - Close handler
 * @param {Object} tierInfo - User's tier information
 * @param {String} tierInfo.tier - Current tier name
 * @param {Number} tierInfo.currentUsage - Current ads used
 * @param {Number} tierInfo.limit - Tier limit
 */
export default function UpgradeLimitModal({ open, onClose, tierInfo }) {
  console.log('[UpgradeLimitModal] Rendered with:', tierInfo);

  const navigate = useNavigate();

  const handleUpgrade = () => {
  console.log('[UpgradeLimitModal] User clicked Upgrade');
  
  // Store flag that we're upgrading to create a listing
  sessionStorage.setItem('upgradingForListing', 'true');
  // ⭐ NEW: Prevent form data from being cleared during navigation
  sessionStorage.setItem('comingFromUpgrade', 'true');
  
  // Navigate to upgrade page
  navigate('/subscription/upgrade');
  onClose();
};

  const handleDeleteAd = () => {
    console.log('[UpgradeLimitModal] User clicked Delete Ad');
    
    // Store flag that we're deleting to make room for new listing
    sessionStorage.setItem('deletingForNewListing', 'true');
    
    // Navigate to my listings page
    navigate('/my-listings', { 
      state: { 
        action: 'delete-for-new',
        message: 'Delete one listing to make room for your new ad'
      } 
    });
    onClose();
  };

  const tierConfig = getTierInfo(tierInfo?.tier);
  const badgeColor = getTierBadgeColor(tierInfo?.tier);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md">
        <DialogHeader className="space-y-1.5 sm:space-y-2">
          <div className="flex items-center gap-2 mb-1 sm:mb-2">
            <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
            <DialogTitle className="text-base sm:text-lg md:text-xl">Listing Limit Reached</DialogTitle>
          </div>
          <DialogDescription className="text-xs sm:text-sm md:text-base">
            You've reached your tier's listing limit. Choose an option to continue.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 sm:space-y-4 py-3 sm:py-4">
          {/* Current Tier Info */}
          <div className="bg-muted p-3 sm:p-4 rounded-lg space-y-1.5 sm:space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-muted-foreground">Current Plan:</span>
              <Badge variant="outline" className={`${badgeColor} text-[10px] sm:text-xs`}>
                {tierConfig.icon} {tierConfig.name}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-muted-foreground">Listings Used:</span>
              <span className="text-xs sm:text-sm font-semibold">
                {tierInfo?.currentUsage || 0} / {tierInfo?.limit || 0}
              </span>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-2 sm:space-y-3">
            {/* Option 1: Upgrade */}
            <div className="border rounded-lg p-3 sm:p-4 hover:bg-muted/50 transition-colors">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="bg-green-100 p-1.5 sm:p-2 rounded-full">
                  <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-700" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-xs sm:text-sm mb-1">Upgrade Your Plan</h4>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mb-2 sm:mb-3">
                    Get more listings and unlock additional features with a higher tier.
                  </p>
                  <Button 
                    size="sm" 
                    className="w-full text-xs sm:text-sm h-8 sm:h-9"
                    onClick={handleUpgrade}
                  >
                    <TrendingUp className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                    View Upgrade Options
                  </Button>
                </div>
              </div>
            </div>

            {/* Option 2: Delete an Ad */}
            <div className="border rounded-lg p-3 sm:p-4 hover:bg-muted/50 transition-colors">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="bg-orange-100 p-1.5 sm:p-2 rounded-full">
                  <Trash2 className="h-4 w-4 sm:h-5 sm:w-5 text-orange-700" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-xs sm:text-sm mb-1">Delete an Existing Ad</h4>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mb-2 sm:mb-3">
                    Remove one of your current listings to make room for this new one.
                  </p>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="w-full text-xs sm:text-sm h-8 sm:h-9"
                    onClick={handleDeleteAd}
                  >
                    <Trash2 className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                    Manage My Listings
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 sm:p-3">
            <p className="text-[10px] sm:text-xs text-blue-800">
              💡 <strong>Tip:</strong> After upgrading or deleting a listing, you'll be able to complete your new listing creation.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-xs sm:text-sm h-9 sm:h-10">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}